import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, RefreshCw, Loader2, Brain, Sparkles, Database, Search,
  TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Density-Learning Admin-Dashboard
 *
 * Zeigt was das System global aus User-Wiegungen über Lebensmittel-
 * Dichten gelernt hat. Datenquelle: `/api/admin/density-learning/stats`.
 *
 * Mehrwert:
 *  - Vertrauens-/Transparenz-Karte für die KI-Genauigkeit
 *  - Schneller Überblick: welche Speisen wurden bereits kalibriert?
 *  - Delta gegen Default (zeigt ob System realistischere Werte gefunden hat)
 *  - Manuelles Cache-Refresh für Debug nach DB-Imports
 */
const DensityLearningPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/density-learning/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (e) {
      console.error('density learning stats error', e);
      toast.error('Statistik konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const refreshCache = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/density-learning/refresh-cache`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Cache neu geladen: ${res.data.loaded} Overrides`);
      await fetchStats();
    } catch (e) {
      toast.error('Cache-Refresh fehlgeschlagen');
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    if (!stats?.overrides) return [];
    const q = search.trim().toLowerCase();
    if (!q) return stats.overrides;
    return stats.overrides.filter((o) => o.food_key.toLowerCase().includes(q));
  }, [stats, search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const totalSamples = stats?.total_samples_collected ?? 0;
  const totalOverrides = stats?.total_overrides_active ?? 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            data-testid="density-learning-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Brain className="w-6 h-6 text-amber-500" />
              KI-Lern-Statistik (LiDAR Dichte-DB)
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Was hat das System aus anonymisierten User-Wiegungen gelernt?
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card data-testid="stat-card-samples">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-blue-50">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Gesammelte Wiegungen</p>
                  <p className="text-2xl font-bold text-gray-900">{totalSamples}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-card-overrides">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-emerald-50">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Aktive Overrides</p>
                  <p className="text-2xl font-bold text-gray-900">{totalOverrides}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-card-refresh">
            <CardContent className="pt-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cache-Status</p>
                <p className="text-xs text-gray-400 mt-1">
                  Lädt beim Backend-Start automatisch
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={refreshCache}
                disabled={refreshing}
                data-testid="refresh-cache-btn"
              >
                {refreshing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {refreshing ? '...' : 'Neu laden'}
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Gelernte Lebensmittel-Dichten</CardTitle>
            <CardDescription>
              Sortiert nach Sample-Anzahl. Ein Override wird aktiv, sobald
              ≥10 Wiegungen von ≥5 verschiedenen Usern mit niedriger Streuung
              vorliegen (Privacy: User-IDs sind sha256-gehashed).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative mb-4">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Lebensmittel suchen (z. B. rice, chicken)"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="density-search-input"
              />
            </div>

            {filtered.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                {totalOverrides === 0 ? (
                  <>
                    <Brain className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">Noch keine globalen Lern-Daten</p>
                    <p className="text-sm mt-1">
                      Sobald 5+ User Mahlzeiten abwiegen und scannen, lernt das
                      System Dichten und zeigt sie hier.
                    </p>
                  </>
                ) : (
                  <p>Keine Treffer für „{search}"</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-600">
                      <th className="py-2 px-2 font-medium">Lebensmittel</th>
                      <th className="py-2 px-2 font-medium text-right">Default</th>
                      <th className="py-2 px-2 font-medium text-right">Gelernt</th>
                      <th className="py-2 px-2 font-medium text-right">Δ</th>
                      <th className="py-2 px-2 font-medium text-right">Samples</th>
                      <th className="py-2 px-2 font-medium text-right">Users</th>
                      <th className="py-2 px-2 font-medium text-right">σ/μ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((o) => (
                      <DensityRow key={o.food_key} row={o} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const DensityRow = ({ row }) => {
  const delta = row.delta_pct ?? 0;
  const trend = delta > 1
    ? { color: 'text-emerald-600', bg: 'bg-emerald-50', Icon: TrendingUp }
    : delta < -1
    ? { color: 'text-rose-600', bg: 'bg-rose-50', Icon: TrendingDown }
    : { color: 'text-gray-500', bg: 'bg-gray-50', Icon: Minus };

  return (
    <tr className="border-b last:border-0 hover:bg-gray-50 transition-colors">
      <td className="py-2 px-2 font-medium text-gray-900">{row.food_key}</td>
      <td className="py-2 px-2 text-right text-gray-500">
        {row.default_density?.toFixed(3)} g/cm³
      </td>
      <td className="py-2 px-2 text-right font-semibold text-gray-900">
        {row.density?.toFixed(3)} g/cm³
      </td>
      <td className="py-2 px-2 text-right">
        <Badge variant="secondary" className={`${trend.bg} ${trend.color} border-0 gap-1`}>
          <trend.Icon className="w-3 h-3" />
          {delta > 0 ? '+' : ''}{delta}%
        </Badge>
      </td>
      <td className="py-2 px-2 text-right text-gray-700">{row.sample_count}</td>
      <td className="py-2 px-2 text-right text-gray-700">{row.unique_users}</td>
      <td className="py-2 px-2 text-right text-xs text-gray-500">
        {(row.stddev_ratio * 100).toFixed(1)}%
      </td>
    </tr>
  );
};

export default DensityLearningPage;
